import type { BLEND_MODES, WebGLRenderer } from 'pixi.js';
import type { BlendMode } from '../core/model';

const NAMES: Record<BlendMode, string> = {
  normal: 'normal',
  additive: 'studio-additive',
  subtract: 'studio-subtract',
  screen: 'studio-screen',
  multiply: 'studio-multiply',
  lighten: 'studio-lighten',
  darken: 'studio-darken',
};

/**
 * Registers the item blend modes as fixed-function blend state on the
 * renderer's state system: [srcRGB, dstRGB, srcA, dstA, eqRGB, eqA] for
 * premultiplied colour. Destination alpha always accumulates as in normal
 * blending (subtract leaves it untouched) so nested scenes and groups keep a
 * correct coverage channel. Must be re-run after a context restore.
 */
export function installBlendModes(renderer: WebGLRenderer): void {
  const gl = renderer.gl;
  const map = (renderer.state as unknown as { blendModesMap: Record<string, number[]> }).blendModesMap;
  const over = [gl.ONE, gl.ONE_MINUS_SRC_ALPHA];
  map[NAMES.additive] = [gl.ONE, gl.ONE, ...over, gl.FUNC_ADD, gl.FUNC_ADD];
  map[NAMES.subtract] = [gl.ONE, gl.ONE, gl.ZERO, gl.ONE, gl.FUNC_REVERSE_SUBTRACT, gl.FUNC_ADD];
  map[NAMES.screen] = [gl.ONE, gl.ONE_MINUS_SRC_COLOR, ...over, gl.FUNC_ADD, gl.FUNC_ADD];
  map[NAMES.multiply] = [gl.DST_COLOR, gl.ONE_MINUS_SRC_ALPHA, ...over, gl.FUNC_ADD, gl.FUNC_ADD];
  map[NAMES.lighten] = [gl.ONE, gl.ONE, ...over, gl.MAX, gl.FUNC_ADD];
  map[NAMES.darken] = [gl.ONE, gl.ONE, ...over, gl.MIN, gl.FUNC_ADD];
}

export function pixiBlendMode(mode: BlendMode): BLEND_MODES {
  return (NAMES[mode] ?? 'normal') as BLEND_MODES;
}
